/**
 * Concrete next-step suggestions for Quick Clarity actions.
 * Prefer AI-provided suggested_actions; fall back to insight fields.
 * CBT / decision-journal register: procedural, testable, not soothing.
 */

function clip(text, max = 140) {
  const s = String(text || "").trim().replace(/\s+/g, " ");
  if (!s) return "";
  if (s.length <= max) return s;
  const cut = s.slice(0, max - 1);
  const sp = cut.lastIndexOf(" ");
  return `${(sp > 40 ? cut.slice(0, sp) : cut).trim()}…`;
}

function uniqActions(list, max = 3) {
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const t = clip(item, 160);
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
    if (out.length >= max) break;
  }
  return out;
}

/**
 * @param {object} insight - Quick Clarity insight payload
 * @returns {string[]}
 */
export function getQuickClaritySuggestedActions(insight) {
  const n = insight && typeof insight === "object" ? insight : {};
  if (n.should_pause) return [];

  const fromAi = Array.isArray(n.suggested_actions)
    ? n.suggested_actions.map((s) => String(s || "").trim()).filter(Boolean)
    : [];
  if (fromAi.length > 0) return uniqActions(fromAi, 3);

  const unknowns = Array.isArray(n.unknowns) ? n.unknowns.map((u) => String(u || "").trim()).filter(Boolean) : [];
  const patternKey = String(n.possible_cbt_pattern?.key || "").toLowerCase();
  const insightText = String(n.primary_insight || "").trim();
  const interpretation = String(n.user_interpretation || "").trim();
  const fallback = [];

  if (unknowns[0]) {
    fallback.push(`Gather one fact about: ${clip(unknowns[0], 90)}`);
  }
  if (insightText) {
    fallback.push(`Test this interpretation with one small check before you respond.`);
  }
  if (patternKey === "mind_reading" || /mind.?read/i.test(patternKey)) {
    fallback.push("Write one observable fact and one inferred motive — keep them separate.");
  } else if (patternKey === "catastrophizing" || patternKey === "fortune_telling") {
    fallback.push("Rate belief in the predicted outcome 0–100, then name one fact that would move that number.");
  } else if (patternKey === "personalization") {
    fallback.push("List factors you control versus factors you do not — pick one controllable next step.");
  } else if (interpretation) {
    fallback.push("Delay the reply by 10 minutes; draft what you would send after checking the evidence.");
  }

  if (fallback.length < 2) {
    fallback.push("Ask one clarifying question instead of filling the gap with an assumption.");
  }
  if (fallback.length < 3) {
    fallback.push("Record evidence for and against the interpretation in two short lists.");
  }

  return uniqActions(fallback, 3);
}
